import { useInputValidation } from '@e2m-customer-portal/hooks';
import { Nullable, Validation, isDefined, isRequired } from '@e2m-customer-portal/shared';
import { DefaultItemRender, DefaultSelectRender } from '@e2m-customer-portal/ui';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { ReactElement, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSidebarMarketLocationsQuery } from '../../../core/sidebar/market-locations/query';
import { AssignedMarketLocation } from '../../../generated/graphql';
import { DialogAccount } from './UserDialog';

interface MarketLocationSelectProps {
  className: string | undefined;
  name: string;
  value: string[] | undefined;
  account: DialogAccount;
  onChange: (account: DialogAccount) => void;
  labelId?: string;
  disabled?: boolean;
  required?: boolean;
  rules?: Nullable<Validation<string>[]>;
}

export function MarketLocationSelect({
  className,
  name,
  value,
  account,
  onChange,
  labelId,
  disabled,
  required = false,
}: Readonly<MarketLocationSelectProps>): ReactElement<MarketLocationSelectProps> {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const { data: marketLocations } = useSidebarMarketLocationsQuery();

  const rules = useMemo(() => {
    const result: Validation<string[]>[] = [];
    if (required) {
      result.push(isRequired(tv('COMMON_REQUIRED')));
    }
    return result;
  }, [required, tv]);

  const { handleBlur, error, message } = useInputValidation(name, rules, value);
  return (
    <>
      <Select
        className={className}
        value={value}
        renderValue={(ids: unknown): ReactNode => (
          <DefaultSelectRender
            options={[]}
            selected={
              Array.isArray(ids)
                ? (ids as string[])
                    .map((id) => marketLocations?.marketLocations.edges.find((edge) => edge.node.id === id)?.node)
                    .filter(isDefined)
                : []
            }
          />
        )}
        labelId={labelId}
        label={t('SETTINGS_USER_MANAGEMENT_MARKET_LOCATIONS')}
        multiple
        required={required}
        onBlur={handleBlur}
        onChange={(event) => {
          onChange({
            ...account,
            marketLocations: (event.target.value as string[])
              .map((id) => {
                const edge = marketLocations?.marketLocations.edges.find((edge) => edge.node.id === id);
                return (
                  edge &&
                  ({
                    id: edge.node.id,
                    name: edge.node.name,
                    externalName: edge.node.externalName,
                  } satisfies AssignedMarketLocation)
                );
              })
              .filter(isDefined),
          });
        }}
        disabled={disabled}
        MenuProps={{ PaperProps: { sx: { maxHeight: '50vh' } } }}
        error={error}
      >
        {marketLocations?.marketLocations.edges.map((edge) => (
          <MenuItem key={edge.node.id} value={edge.node.id}>
            <DefaultItemRender
              value={edge.node.externalName || edge.node.name}
              selected={account.marketLocations?.some((marketLocation) => marketLocation.id === edge.node.id) ?? false}
            />
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText className="text-error-500">{message}</FormHelperText>}
    </>
  );
}
